import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ConfigurationTutorialModule } from './configurationTutorial';
import styles from './QuickStartTutorial.module.css';
import type { TutorialBounds as Bounds } from './tutorialPosition';
import { cardAnchor, positionCardNearTarget } from './tutorialPosition';

type Step = 0 | 1 | 2 | 3;

const modules: Record<
  ConfigurationTutorialModule,
  {
    label: string;
    createLabel: string;
    fieldTitle: string;
    relationTitle: string;
    relationText: string;
  }
> = {
  devices: {
    label: '设备',
    createLabel: '新增设备',
    fieldTitle: '设备名称',
    relationTitle: '设备人工价格维护',
    relationText: '核对设备价格及其他人工维护信息，字段会按当前账号权限显示。',
  },
  packages: {
    label: '套餐',
    createLabel: '新增套餐',
    fieldTitle: '套餐名称',
    relationTitle: '关联设备',
    relationText: '选择要放入套餐的设备并核对数量，套餐价格会按关联内容汇总。',
  },
  plans: {
    label: '方案',
    createLabel: '新增方案',
    fieldTitle: '方案名称',
    relationTitle: '设备套餐',
    relationText: '选择要放入方案的设备套餐，再核对方案折扣和方案价。',
  },
};

const visible = (element: HTMLElement | null): element is HTMLElement => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden'
  );
};

function findOpenDrawer(module: ConfigurationTutorialModule) {
  const title = modules[module].createLabel;
  return (
    Array.from(document.querySelectorAll<HTMLElement>('.ant-drawer-open')).find(
      (drawer) => visible(drawer) && drawer.textContent?.includes(title),
    ) ?? null
  );
}

function findTextElement(root: ParentNode, selector: string, text: string) {
  return (
    Array.from(root.querySelectorAll<HTMLElement>(selector)).find(
      (element) =>
        visible(element) && element.textContent?.trim().includes(text),
    ) ?? null
  );
}

function findTarget(module: ConfigurationTutorialModule, step: Step) {
  const copy = modules[module];
  if (step === 0) {
    return (
      Array.from(document.querySelectorAll<HTMLElement>('main button')).find(
        (button) =>
          visible(button) && button.textContent?.trim() === copy.createLabel,
      ) ?? null
    );
  }

  const drawer = findOpenDrawer(module);
  if (!drawer) return null;
  if (step === 1) {
    const nameInput = drawer.querySelector<HTMLElement>('#name');
    const nameItem = nameInput?.closest<HTMLElement>('.ant-form-item');
    if (nameItem && visible(nameItem)) return nameItem;
    return findTextElement(drawer, '.ant-form-item', copy.fieldTitle);
  }
  if (step === 2) {
    if (module === 'plans') {
      const packageInput = drawer.querySelector<HTMLElement>(
        '#packageRevisionIds',
      );
      const packageItem = packageInput?.closest<HTMLElement>('.ant-form-item');
      if (packageItem && visible(packageItem)) return packageItem;
      return findTextElement(drawer, '.ant-form-item', copy.relationTitle);
    }
    return findTextElement(drawer, '.ant-card', copy.relationTitle);
  }
  return (
    Array.from(drawer.querySelectorAll<HTMLButtonElement>('button')).find(
      (button) =>
        visible(button) && button.textContent?.replace(/\s+/g, '') === '保存',
    ) ?? null
  );
}

export function AdminConfigurationTutorial({
  module,
  onClose,
}: {
  module: ConfigurationTutorialModule | null;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>(0);
  const [closed, setClosed] = useState(false);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [missingTarget, setMissingTarget] = useState(false);
  const [targetDisabled, setTargetDisabled] = useState(false);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [cardHeight, setCardHeight] = useState(230);
  const card = useRef<HTMLElement>(null);
  const opened = useRef(false);
  const focusedEntry = useRef(false);
  const maskId = useId();

  useEffect(() => {
    setStep(0);
    setClosed(false);
    opened.current = false;
    focusedEntry.current = false;
  }, [module]);

  useEffect(() => {
    if (!module) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [module, onClose]);

  useLayoutEffect(() => {
    if (!module) return;
    let frame = 0;
    const measure = () => {
      const drawer = findOpenDrawer(module);
      if (!opened.current && drawer) {
        opened.current = true;
        setStep(1);
        return;
      }
      if (opened.current && !drawer && !closed) {
        setClosed(true);
        return;
      }

      const target = closed ? null : findTarget(module, step);
      if (
        step === 0 &&
        target &&
        !focusedEntry.current &&
        target instanceof HTMLButtonElement
      ) {
        target.focus({ preventScroll: true });
        focusedEntry.current = true;
      }
      setMissingTarget(!target);
      setTargetDisabled(
        Boolean(target instanceof HTMLButtonElement && target.disabled),
      );
      const rect = target?.getBoundingClientRect();
      setBounds(
        rect && rect.width > 0 && rect.height > 0
          ? {
              left: Math.max(4, rect.left - 5),
              top: Math.max(4, rect.top - 5),
              right: Math.min(window.innerWidth - 4, rect.right + 5),
              bottom: Math.min(window.innerHeight - 4, rect.bottom + 5),
            }
          : null,
      );
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      if (card.current)
        setCardHeight(card.current.getBoundingClientRect().height);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const initialTarget = findTarget(module, step);
    if (initialTarget && step > 0)
      initialTarget.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    measure();
    const mutation = new MutationObserver(schedule);
    mutation.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'disabled'],
    });
    const resize = new ResizeObserver(schedule);
    if (initialTarget) resize.observe(initialTarget);
    if (card.current) resize.observe(card.current);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      cancelAnimationFrame(frame);
      mutation.disconnect();
      resize.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [module, step, closed]);

  useLayoutEffect(() => {
    if (!module) return;
    const target = findTarget(module, step);
    const focusable = target?.matches(
      'button, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    )
      ? target
      : target?.querySelector<HTMLElement>(
          'input:not(:disabled), textarea:not(:disabled), button:not(:disabled), [role="combobox"], [tabindex="0"]',
        );
    (focusable ?? card.current)?.focus({ preventScroll: true });
  }, [module, step]);

  if (!module) return null;

  const copy = modules[module];
  const cardWidth = Math.min(360, viewport.width - 32);
  const position = positionCardNearTarget(bounds, viewport, {
    width: cardWidth,
    height: cardHeight,
  });
  const { left, top, side } = position;
  const currentStep = closed ? 4 : step + 1;
  const content = closed
    ? {
        title: '表单已关闭',
        text: '教程不会判断表单是否已保存，请查看页面提示或返回目录核对结果。',
        hint: '教程不会自动提交或清理当前内容。',
      }
    : step === 0
      ? {
          title: `开始新建${copy.label}`,
          text: `点击「${copy.createLabel}」打开新建表单。`,
          hint: '教程不会代替你填写或保存；你可以随时退出。',
        }
      : step === 1
        ? {
            title: '填写基础资料',
            text: `先填写${copy.fieldTitle}等基础信息，再按表单提示补齐必填项。`,
            hint: '红色星号表示必填，字段以当前账号可见内容为准。',
          }
        : step === 2
          ? {
              title: `配置${copy.relationTitle}`,
              text: copy.relationText,
              hint: '请按真实业务资料配置，不需要使用教程示例数据。',
            }
          : {
              title: '核对并保存',
              text: '确认基础资料和关联内容后，由你手动点击「保存」提交。',
              hint: '教程不会自动填写或保存；完成讲解也不会关闭表单。',
            };
  const hint = missingTarget
    ? closed
      ? content.hint
      : targetDisabled
        ? '当前账号没有新建权限，无法继续操作此表单。'
        : step === 0
          ? '当前页面尚未显示新增入口，检查模块权限后可重新打开教程。'
          : '此字段可能受账号权限或字段配置影响而未显示，可按页面实际内容继续。'
    : content.hint;
  const waiting = targetDisabled
    ? '当前账号无法新建'
    : missingTarget
      ? step === 0
        ? '等待新增入口载入'
        : '等待对应表单内容'
      : '等待打开新建表单';
  const markerX = bounds
    ? side === 'right'
      ? Math.min(viewport.width - 20, bounds.right + 18)
      : side === 'left'
        ? Math.max(20, bounds.left - 18)
        : Math.max(
            20,
            Math.min(viewport.width - 20, (bounds.left + bounds.right) / 2),
          )
    : left + cardWidth / 2;
  const markerY = bounds
    ? side === 'top'
      ? Math.max(20, bounds.top - 18)
      : side === 'bottom'
        ? Math.min(viewport.height - 20, bounds.bottom + 18)
        : Math.max(
            20,
            Math.min(viewport.height - 20, (bounds.top + bounds.bottom) / 2),
          )
    : top;
  const anchor = cardAnchor(
    side,
    { x: markerX, y: markerY },
    { left, top, width: cardWidth, height: cardHeight },
  );
  const leaderLength = Math.hypot(anchor.x - markerX, anchor.y - markerY);
  const overlapsTarget = Boolean(
    bounds &&
      left < bounds.right &&
      left + cardWidth > bounds.left &&
      top < bounds.bottom &&
      top + cardHeight > bounds.top,
  );
  const portalRoot = findOpenDrawer(module) ?? document.body;

  return createPortal(
    <div className={`${styles.layer} ${styles.adminLayer}`}>
      {bounds ? (
        <svg
          className={styles.spotlight}
          width="100%"
          height="100%"
          aria-hidden="true"
        >
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={bounds.left}
                y={bounds.top}
                width={bounds.right - bounds.left}
                height={bounds.bottom - bounds.top}
                rx="5"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="var(--tutorial-scrim)"
            mask={`url(#${maskId})`}
          />
          <rect
            key={`admin-${module}-${currentStep}`}
            className={styles.outline}
            x={bounds.left}
            y={bounds.top}
            width={bounds.right - bounds.left}
            height={bounds.bottom - bounds.top}
            rx="5"
          />
          {!overlapsTarget && leaderLength <= 120 ? (
            <path
              className={styles.leader}
              d={`M ${markerX} ${markerY} L ${anchor.x} ${anchor.y}`}
            />
          ) : null}
          <circle cx={markerX} cy={markerY} r="15" className={styles.marker} />
          <text
            x={markerX}
            y={markerY}
            className={styles.markerText}
            dominantBaseline="central"
            textAnchor="middle"
          >
            {currentStep}
          </text>
        </svg>
      ) : null}
      <section
        ref={card}
        aria-label={`后台配置教程：${copy.label}，第 ${currentStep} 步，共 4 步`}
        aria-live="polite"
        tabIndex={-1}
        className={styles.card}
        style={{ left, top, width: cardWidth }}
      >
        <div className={styles.heading}>
          <span>
            后台配置 · {copy.label}
            <span className={styles.progress}>{currentStep} / 4</span>
          </span>
          <button
            type="button"
            className={styles.close}
            aria-label="关闭教程"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        </div>
        <div
          key={`admin-${module}-${currentStep}-${content.title}`}
          className={styles.stepContent}
        >
          <h2>{content.title}</h2>
          <p>{content.text}</p>
          <p className={styles.hint}>{hint}</p>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.textButton} onClick={onClose}>
            退出教程
          </button>
          <div className={styles.actions}>
            {!closed && step > 1 ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => setStep((current) => (current - 1) as Step)}
              >
                上一步
              </button>
            ) : null}
            {!closed && step > 0 && step < 3 ? (
              <button
                type="button"
                className={styles.complete}
                onClick={() => setStep((current) => (current + 1) as Step)}
              >
                下一步
              </button>
            ) : closed || step === 3 ? (
              <button
                type="button"
                className={styles.complete}
                onClick={onClose}
              >
                完成讲解
              </button>
            ) : (
              <span className={styles.waiting}>{waiting}</span>
            )}
          </div>
        </div>
      </section>
    </div>,
    portalRoot,
  );
}
